/**
 * World layout as plain data (no three.js) so content, systems and tests can share it.
 * Axes: +x = east, +z = south (toward the highway and the strip of stores).
 */
export interface Vec2 {
  x: number;
  z: number;
}

export interface Rect {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

export const rect = (minX: number, minZ: number, maxX: number, maxZ: number): Rect => ({
  minX,
  minZ,
  maxX,
  maxZ,
});

export const inRect = (r: Rect, x: number, z: number, pad = 0): boolean =>
  x >= r.minX - pad && x <= r.maxX + pad && z >= r.minZ - pad && z <= r.maxZ + pad;

export const MAIN_STREET = rect(-3.5, -51.5, 3.5, 56);
/** Runs from Possum Trot Lane, across the top of Main Street, to the junkyard. */
export const NORTH_ROAD = rect(-67.5, -51.5, 40, -44.5);
/** The second street, west of Main Street (centerline x = -64). */
export const POSSUM_TROT = rect(-67.5, -51.5, -60.5, 40);
/** Possum Trot keeps going north as a dirt road into the Back Forty. */
export const BACK_FORTY_ROAD = rect(-67.5, -107, -60.5, -51.5);
/** The bad side of the park. Lot 13 is back there. Don't ask. */
export const BACK_FORTY = rect(-108, -112, -28, -53);
/** Cookout ground at the south end of Possum Trot. */
export const COMMONS = rect(-86, 40.5, -42, 52.5);
/** The crick: a muddy creek across the Back Forty. Crossed by a plank bridge on the road. */
export const CRICK = { minX: -112, maxX: -24, z: -92, width: 4.2 } as const;

/** 0 outside the Back Forty, ramping up to 1 a few meters past its edge. */
export function backFortyDepth(x: number, z: number): number {
  const b = BACK_FORTY;
  const inside = Math.min(x - b.minX, b.maxX - x, z - b.minZ, b.maxZ - z);
  return Math.min(1, Math.max(0, (inside + 4) / 10));
}

/** The crick's centerline wanders a little. */
export const crickZ = (x: number): number =>
  CRICK.z + Math.sin(x * 0.11) * 1.4 + Math.sin(x * 0.037 + 1) * 1.1;
export const HIGHWAY = rect(-140, 56, 140, 66);
export const STORE_LOT = rect(-46, 67, 38, 81);
/** West of the Gas-N-Grub: the Booze Barn and the Rusty Spur share a gravel lot. */
export const WEST_LOT = rect(-108, 67, -47, 79);
/** Cooter's Used Cars, east of Lucky's Pawn. */
export const CAR_LOT = rect(42, 67, 86, 92);
/** Fairgrounds Road runs north from the highway to the rodeo, the mud bog and the gun range. */
export const FAIRGROUNDS_ROAD = rect(54, -40, 61, 56);
/** The rodeo arena: an oval of fence around a dirt ring. */
export const RODEO = { x: 80, z: 18, rx: 15, rz: 11 } as const;
/** The mud bog: drive in, get stuck, get famous. */
export const MUD_BOG = rect(64, -30, 100, -10);
/** Bubba's Gun Range, past the junkyard. Shoot north, at cans, into a dirt berm. */
export const GUN_RANGE = rect(72, -108, 104, -72);

/** Out at the fairgrounds proper, around the rodeo arena and the mud bog (the music changes). */
export function inFairgrounds(x: number, z: number): boolean {
  const dx = (x - RODEO.x) / (RODEO.rx + 12);
  const dz = (z - RODEO.z) / (RODEO.rz + 12);
  return dx * dx + dz * dz < 1 || inRect(MUD_BOG, x, z, 8);
}

/** How deep in mud a point is (0 = dry ground, 1 = axle deep). */
export function mudAt(x: number, z: number): number {
  const b = MUD_BOG;
  const inside = Math.min(x - b.minX, b.maxX - x, z - b.minZ, b.maxZ - z);
  if (inside <= 0) return 0;
  // Deepest in the middle third; ruts make it uneven.
  const ruts = 0.85 + 0.15 * Math.sin(x * 0.9) * Math.sin(z * 1.3);
  return Math.min(1, inside / 4) * ruts;
}

export const LOT_ROWS = [30, 14, -2, -18, -34] as const;
export const TRAILER_LENGTH = 12;
export const TRAILER_WIDTH = 4;

export type LotSide = -1 | 1;

export interface LotInfo {
  id: string;
  number: number;
  side: LotSide;
  z: number;
  /** Trailer center. */
  trailer: Vec2;
  /** Front door (on the trailer's +z wall, toward the street end). */
  door: Vec2;
  /** A porch deck out front instead of cinder-block steps. */
  deck: boolean;
  /** Where you stand to knock: the foot of the steps (you can't climb onto a deck). */
  stoop: Vec2;
}

/** A porch deck sticks out this far from the trailer, and it's solid all the way. */
export const DECK_DEPTH = 2.4;
/** Cinder-block steps stick out this far. */
export const STEPS_DEPTH = 0.9;

/** Where you stand to knock on a door with a deck (or just steps) in front of it: just clear of it. */
export const stoopAt = (door: Vec2, deck: boolean): Vec2 => ({
  x: door.x,
  z: door.z + (deck ? DECK_DEPTH : STEPS_DEPTH) + 0.45,
});

function lot(id: string, number: number, side: LotSide, z: number, deck = false): LotInfo {
  const cx = side * 16.5;
  const door = { x: side * 12.5, z: z - 2.5 };
  return {
    id,
    number,
    side,
    z,
    trailer: { x: cx, z: z - 4.5 },
    door,
    deck,
    stoop: stoopAt(door, deck),
  };
}

export const LOTS = {
  tammy: lot('tammy', 5, -1, 30, true),
  earl: lot('earl', 6, 1, 30),
  player: lot('player', 7, -1, 14),
  dale: lot('dale', 8, 1, 14),
  jenkins: lot('jenkins', 9, -1, -2, true),
  vacant: lot('vacant', 10, 1, -2),
  skeeter: lot('skeeter', 11, -1, -18),
  brenda: lot('brenda', 12, 1, -18, true),
  // There is no lot 13. Darlene "doesn't need that kind of energy."
  wanda: lot('wanda', 14, -1, -34),
  hank: lot('hank', 15, 1, -34),
} as const;

export type LotId = keyof typeof LOTS;

/** Named points of interest used by interactions, NPCs, events and jobs. */
export const SPOTS = {
  playerDoor: { x: -12.5, z: 12.2 },
  playerCouch: { x: -15.5, z: 12.7 },
  playerGrill: { x: -19.6, z: 13.1 },
  playerMailbox: { x: -4.7, z: 13.6 },
  camaro: { x: -8.2, z: 17 },
  spawn: { x: -12.6, z: 14.2 },

  office: { x: -9.0, z: 46 },
  officeCenter: { x: -14, z: 46 },
  parkSign: { x: -7.5, z: 53 },
  dumpster: { x: -7.5, z: 40.2 },
  bigDave: { x: 24, z: 53.4 },
  bigDaveHome: { x: 24, z: 46 },
  tubeMan: { x: 12, z: 47.5 },

  gasDoor: { x: -30, z: 81.0 },
  gasCanopy: { x: -30, z: 74 },
  payphone: { x: -20.6, z: 81.0 },
  laundromatDoor: { x: 0.8, z: 81.0 },
  jobBoard: { x: -5.5, z: 81.0 },
  pawnDoor: { x: 23, z: 81.0 },

  junkyardShack: { x: 43.5, z: -55 },
  junkyardWindow: { x: 43.5, z: -52.2 },
  junkyardDrop: { x: 46, z: -46 },
  junkyardGate: { x: 38, z: -48 },

  earlGrill: { x: 14, z: 31.6 },
  earlFirePit: { x: 19.5, z: 34 },
  tankHouse: { x: 23.5, z: 33.5 },
  jenkinsChair: { x: -13.4, z: -3.4 },
  skeeterRamp: { x: -8.5, z: -11.5 },
  bmxField: { x: -14, z: -62 },

  // Possum Trot Lane & the Commons
  possumTrotSign: { x: -59.4, z: 38.6 },
  cornholeA: { x: -75, z: 46.5 },
  cornholeB: { x: -66, z: 46.5 },
  ricky: { x: -78.2, z: 44.2 },
  picnicTable: { x: -55.5, z: 46.5 },
  commonsFirePit: { x: -50.5, z: 44.5 },
  condemned: { x: -47.5, z: -38.5 },
  meemaw: { x: -77.3, z: -35.2 },
  meemawDoor: { x: -76.5, z: -36.5 },
  meemawStoop: stoopAt({ x: -76.5, z: -36.5 }, true),
  condemnedDoor: { x: -51.5, z: -36.2 },

  // The Back Forty
  backFortySign: { x: -58.6, z: -56 },
  lot13: { x: -47.5, z: -70.5 },
  lot13Door: { x: -51.5, z: -67.6 },
  abandonedA: { x: -80.5, z: -70.5 },
  abandonedADoor: { x: -76.5, z: -67.8 },
  abandonedB: { x: -46.5, z: -84 },
  abandonedBDoor: { x: -50.8, z: -81.3 },
  barrelCamp: { x: -79, z: -83 },
  terry: { x: -76.6, z: -84.6 },
  waterTower: { x: -95, z: -76 },
  peteBus: { x: -47, z: -101.5 },
  pete: { x: -54, z: -99.5 },
  still: { x: -96, z: -104.5 },
  junkPile: { x: -80, z: -103 },
  // Catfish holes along the crick banks (where you stand to reach in).
  noodleA: { x: -84, z: crickZ(-84) + 3.4 },
  noodleB: { x: -42, z: crickZ(-42) + 3.4 },
  noodleC: { x: -33, z: crickZ(-33) - 3.4 },

  // South strip, west: liquor and the bar.
  boozeBarn: { x: -62, z: 84 },
  boozeDoor: { x: -62, z: 79.2 },
  rustySpur: { x: -91, z: 85.5 },
  barDoor: { x: -91, z: 79.4 },
  // Cooter's Used Cars.
  cooterOffice: { x: 78, z: 86 },
  cooter: { x: 74.5, z: 82.5 },
  carLotSign: { x: 52, z: 68.2 },
  // The fairgrounds.
  fairgroundsSign: { x: 63, z: 53.5 },
  rodeoChute: { x: 66.4, z: 15.5 },
  rodeoRandy: { x: 64.5, z: 13.5 },
  bleachers: { x: 80, z: 33 },
  mudStart: { x: 66.5, z: -20 },
  mudFinish: { x: 97.5, z: -20 },
  mudSign: { x: 63, z: -32.5 },
  gunCounter: { x: 88, z: -73.5 },
  gus: { x: 91, z: -74.5 },
} as const satisfies Record<string, Vec2>;

export type SpotId = keyof typeof SPOTS;

/** Lawns that can be mowed for cash. */
export const MOW_SITES: Record<string, { owner: string; area: Rect }> = {
  // Each lawn avoids parked trucks, fences and decks.
  jenkins: { owner: 'Old Man Jenkins', area: rect(-25, -1.4, -11.2, 5) },
  tammy: { owner: 'Tammy Lynn', area: rect(-26, 29.2, -16, 35.4) },
  office: { owner: 'Darlene', area: rect(-29, 42, -19, 51) },
};

/** Where junk for hauling jobs gets picked up. */
export const HAUL_SITES: Record<string, Vec2 & { owner: string }> = {
  tammy: { x: -21, z: 34.5, owner: 'Tammy Lynn' },
  dale: { x: 21, z: 18.5, owner: 'Cousin Dale' },
  brenda: { x: 12.5, z: -13.5, owner: 'Brenda' },
  hank: { x: 22, z: -28.5, owner: 'Hank' },
  // Darlene's cleaning out the condemned trailer; nobody claims Lot 13's junk.
  condemned: { x: -53.5, z: -33.5, owner: 'Darlene' },
  lot13: { x: -55, z: -64.5, owner: 'Lot 13' },
};

/** Areas the park's random litter (cans, scrap) can appear in. */
export const LITTER_AREAS: readonly Rect[] = [
  rect(-28, -42, 28, 38),
  rect(-30, 38, 30, 54),
  rect(-40, 67, 36, 81),
  rect(40, -60, 70, -36),
  rect(-60, -42, -56, 38),
  rect(-100, 68, -50, 78),
  rect(44, 68, 70, 78),
];

/** The Back Forty is where the good junk is. */
export const BACK_FORTY_LITTER: readonly Rect[] = [rect(-100, -88, -36, -58), rect(-100, -108, -36, -96)];

export const PARK_BOUNDS = rect(-104, -112, 104, 110);
